import type React from "react";
import { useState } from "react";

import type {
  ReportSource,
  ReportSourceField,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";
import { getFilterDefinitionsForObject } from "@/app/app/_/views/RulesView/utils/filterDefinitions";
import type { FilterDefinitionType } from "@/components/@types";
import { Popper } from "@/components/atoms/Popper";
import { List } from "@/components/organisms/List";
import { NodeList } from "@/components/organisms/Node/NodeList";
import { Select } from "@/components/molecules/Select";
import {
  getReferenceValueOptionsByTypename,
  objectForReportSource,
} from "@/objects";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

/** One choice in a word's menu. */
export type WordOption = {
  value: string;
  label: string;
  note?: string | null;
  /** What this option is one of, said once above the run of them. */
  group?: string | null;
};

/**
 * One live word of the sentence.
 *
 * Set in the card's own title type, so the line reads as a title rather than
 * as a row of inputs, and carrying its own affordance: underlined where it
 * can be changed, lit under the pointer, pressed in when it is clicked. The
 * menu is the app's own list in the app's own popper - the same one every
 * other picker here opens - rather than a second kind of dropdown.
 */
export function Word({
  value,
  options,
  onChange,
  /** A name mid-sentence is said in passing; in a menu it is a heading. */
  isMidSentence = true,
  /**
   * What the word says, when that is not whatever is chosen: a word that adds
   * a line to the sentence says "Add", not the last thing added.
   */
  anchorLabel,
  /** Said, not offered: the sentence as it stands, where it cannot be changed. */
  isReadOnly = false,
}: {
  value: string;
  options: WordOption[];
  onChange: (value: string) => void;
  isMidSentence?: boolean;
  anchorLabel?: React.ReactNode;
  isReadOnly?: boolean;
}) {
  const chosen = options.find((option) => option.value === value) ?? null;

  if (isReadOnly) {
    return (
      <span
        className={cn(
          "inline text-sm [font-weight:600] text-foreground",
          isMidSentence && "lowercase",
        )}
      >
        {anchorLabel ?? chosen?.label ?? value}
      </span>
    );
  }

  return (
    <Select<WordOption>
      value={chosen}
      data={options}
      getOptionKey={(option) => option.value}
      onChange={(option) => onChange(option.value)}
      size="sm"
      popperMinWidth="180px"
      anchor={
        <span
          // A word is chosen, not dragged: its press is its own, and the card
          // stays where it is.
          onPointerDown={(event) => event.stopPropagation()}
          className={cn(
            "inline text-sm [font-weight:600] text-foreground rounded-sm pl-0.5 pr-0.5 -ml-0.5 -mr-0.5 cursor-pointer underline [text-decoration-thickness:initial] decoration-dotted decoration-border underline-offset-[3px] [transition:background-color_0.12s_ease] hover:bg-muted active:bg-accent",
            isMidSentence && "lowercase",
          )}
        >
          {anchorLabel ?? chosen?.label ?? value}
        </span>
      }
    >
      <Select.Options>
        {(option: WordOption, index: number) => (
          <>
            {/* A run of options belonging together is said to belong together,
                once, over the first of them. */}
            {option.group && option.group !== options[index - 1]?.group && (
              <List.ItemGroup value={option.group} />
            )}
            <Select.Option value={option}>
              <div className="flex flex-col gap-px">
                <Typography level="body-md">{option.label}</Typography>
                {option.note && (
                  <Typography
                    level="body-sm"
                    className="text-muted-foreground/80 max-w-65"
                  >
                    {option.note}
                  </Typography>
                )}
              </div>
            </Select.Option>
          </>
        )}
      </Select.Options>
    </Select>
  );
}

/**
 * A figure typed into the sentence, as a word of it: the app's own input would
 * stand taller than the words beside it, so it is written as one - underlined
 * by the text's own decoration, the way every other word is.
 */
export const WORD_INPUT_CLASS =
  "text-sm [font-weight:600] text-foreground bg-transparent [border:none] [outline:none] p-0 underline decoration-dotted decoration-border underline-offset-[3px] placeholder:text-muted-foreground/80 placeholder:[font-weight:400] focus:decoration-solid";

/**
 * A word that says several choices at once - "bills and receipts" - ticked in
 * its menu, which stays open while they are ticked.
 */
export function Words({
  values,
  options,
  onChange,
  placeholder = "…",
  isReadOnly = false,
}: {
  values: string[];
  options: WordOption[];
  onChange: (values: string[]) => void;
  /** What it says with nothing chosen. */
  placeholder?: string;
  isReadOnly?: boolean;
}) {
  const isOpenState = useState(false);
  const labels = options
    .filter((option) => values.includes(option.value))
    .map((option) => option.label.toLowerCase());
  // "bills, receipts and checks"
  const said =
    labels.length > 1
      ? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`
      : (labels[0] ?? placeholder);

  if (isReadOnly) {
    return (
      <span className="inline text-sm [font-weight:600] text-foreground">
        {said}
      </span>
    );
  }

  return (
    <Popper
      isOpenState={isOpenState}
      placement="bottom-start"
      anchor={
        <span
          onPointerDown={(event) => event.stopPropagation()}
          className="inline text-sm [font-weight:600] text-foreground rounded-sm pl-0.5 pr-0.5 -ml-0.5 -mr-0.5 cursor-pointer underline [text-decoration-thickness:initial] decoration-dotted decoration-border underline-offset-[3px] [transition:background-color_0.12s_ease] hover:bg-muted active:bg-accent"
        >
          {said}
        </span>
      }
    >
      <div className="min-w-55">
        <List<WordOption>
          data={options}
          getRowKey={(option) => option.value}
          getIsChecked={(option) => values.includes(option.value)}
          isMultiselect
          isCheckboxesEnabled
          onSelectMultiSelectOption={(option) =>
            onChange(
              values.includes(option.value)
                ? values.filter((value) => value !== option.value)
                : [...values, option.value],
            )
          }
          autofocus
          fillHeight={false}
        >
          <List.Items<WordOption>>
            {(option) => (
              <List.Item value={option}>
                <div className="flex flex-col gap-px">
                  <Typography level="body-md">{option.label}</Typography>
                  {option.note && (
                    <Typography
                      level="body-sm"
                      className="text-muted-foreground/80 max-w-65"
                    >
                      {option.note}
                    </Typography>
                  )}
                </div>
              </List.Item>
            )}
          </List.Items>
        </List>
      </div>
    </Popper>
  );
}

/**
 * A figure of the sentence - "within 5 days" - typed where it stands, and kept
 * once the typing is done: a half-typed "0." is not a tolerance.
 */
export function NumberWord({
  value,
  onChange,
  step = 1,
  isReadOnly = false,
  testId,
}: {
  value: number | string | null | undefined;
  onChange: (value: number | null) => void;
  step?: number;
  isReadOnly?: boolean;
  testId?: string;
}) {
  const said =
    value === null || value === undefined || value === ""
      ? ""
      : String(Number(value));
  const [typing, setTyping] = useState<string | null>(null);

  if (isReadOnly) {
    return (
      <span className="inline text-sm [font-weight:600] text-foreground">
        {said || "…"}
      </span>
    );
  }

  const keep = () => {
    if (typing === null) return;
    const kept = typing.trim() === "" ? null : Number(typing);
    setTyping(null);
    if (kept !== null && Number.isNaN(kept)) return;
    if (kept !== (said === "" ? null : Number(said))) onChange(kept);
  };

  return (
    <input
      type="text"
      inputMode={step < 1 ? "decimal" : "numeric"}
      data-testid={testId}
      value={typing ?? said}
      placeholder="…"
      onFocus={() => setTyping(said)}
      onBlur={keep}
      onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        setTyping(
          event.target.value.replace(step < 1 ? /[^\d.-]/g : /[^\d-]/g, ""),
        )
      }
      onPointerDown={(event: React.PointerEvent) => event.stopPropagation()}
      className={WORD_INPUT_CLASS}
      style={{ width: `${Math.max(2, (typing ?? said).length + 1)}ch` }}
    />
  );
}

/** A record as a sentence names it: its identity kept, its name said. */
export type WordRecord = {
  id?: string | null;
  identifier: string;
  name?: string | null;
};

/**
 * One record, picked where the sentence names it - "post it to Office
 * supplies" - from the list its kind keeps of its own.
 */
export function RecordWord({
  typename,
  value,
  onChange,
  entityIdentifiers,
  placeholder = "…",
  isReadOnly = false,
  testId,
}: {
  typename: string;
  value: WordRecord | null | undefined;
  onChange: (value: WordRecord | null) => void;
  entityIdentifiers?: string[];
  placeholder?: string;
  isReadOnly?: boolean;
  testId?: string;
}) {
  const isOpenState = useState(false);
  const [, setIsOpen] = isOpenState;
  const said = value?.name || placeholder;

  if (isReadOnly) {
    return (
      <span className="inline text-sm [font-weight:600] text-foreground">
        {said}
      </span>
    );
  }

  return (
    <Popper
      isOpenState={isOpenState}
      placement="bottom-start"
      anchor={
        <span
          data-testid={testId}
          onPointerDown={(event) => event.stopPropagation()}
          className={cn(
            "inline text-sm [font-weight:600] text-foreground rounded-sm pl-0.5 pr-0.5 -ml-0.5 -mr-0.5 cursor-pointer underline [text-decoration-thickness:initial] decoration-dotted decoration-border underline-offset-[3px] [transition:background-color_0.12s_ease] hover:bg-muted active:bg-accent",
            value?.name ? "" : "text-muted-foreground/80 font-medium",
          )}
        >
          {said}
        </span>
      }
    >
      <div className="min-w-65 max-h-80 flex">
        <NodeList
          {...getReferenceValueOptionsByTypename(typename)(
            entityIdentifiers,
            "filter",
          )}
          fillHeight={false}
          onSelectOption={(record: WordRecord) => {
            onChange({
              id: record.id ?? null,
              identifier: record.identifier,
              name: record.name ?? null,
            });
            setIsOpen(false);
          }}
          className="max-w-none"
        />
      </div>
    </Popper>
  );
}

/**
 * Several records named together - "Acme and Northwind" - ticked in the list
 * their kind keeps, which stays open while they are ticked.
 */
export function RecordWords({
  typename,
  values,
  onChange,
  entityIdentifiers,
  placeholder = "…",
  isReadOnly = false,
  testId,
}: {
  typename: string;
  values: WordRecord[];
  onChange: (values: WordRecord[]) => void;
  entityIdentifiers?: string[];
  placeholder?: string;
  isReadOnly?: boolean;
  testId?: string;
}) {
  const isOpenState = useState(false);
  const names = values.map((one) => one.name || "…");
  const said =
    names.length > 1
      ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
      : (names[0] ?? placeholder);
  const chosen = new Set(values.map((one) => one.identifier));

  if (isReadOnly) {
    return (
      <span className="inline text-sm [font-weight:600] text-foreground">
        {said}
      </span>
    );
  }

  return (
    <Popper
      isOpenState={isOpenState}
      placement="bottom-start"
      anchor={
        <span
          data-testid={testId}
          onPointerDown={(event) => event.stopPropagation()}
          className={cn(
            "inline text-sm [font-weight:600] text-foreground rounded-sm pl-0.5 pr-0.5 -ml-0.5 -mr-0.5 cursor-pointer underline [text-decoration-thickness:initial] decoration-dotted decoration-border underline-offset-[3px] [transition:background-color_0.12s_ease] hover:bg-muted active:bg-accent",
            values.length ? "" : "text-muted-foreground/80 font-medium",
          )}
        >
          {said}
        </span>
      }
    >
      <div className="min-w-65 max-h-80 flex">
        <NodeList
          {...getReferenceValueOptionsByTypename(typename)(
            entityIdentifiers,
            "filter",
          )}
          fillHeight={false}
          isMultiselect
          isCheckboxesEnabled
          getIsChecked={(record: WordRecord) => chosen.has(record.identifier)}
          onSelectMultiSelectOption={(record: WordRecord) =>
            onChange(
              chosen.has(record.identifier)
                ? values.filter((one) => one.identifier !== record.identifier)
                : [
                    ...values,
                    {
                      id: record.id ?? null,
                      identifier: record.identifier,
                      name: record.name ?? null,
                    },
                  ],
            )
          }
          className="max-w-none"
        />
      </div>
    </Popper>
  );
}

/**
 * The columns a comparison can be made on, gathered by what they hold.
 *
 * A source has dozens of them, and a flat list of dozens is read by scrolling
 * rather than by looking: money with money, dates with dates, and the records
 * a row points at together. What each column holds is the object's own
 * business - every record type in ``@/objects`` declares which report source
 * its rows are - so it is read from there rather than described a second time
 * by the server.
 */
export function fieldsByWhatTheyHold(
  fields: ReportSourceField[],
  source: ReportSource | undefined,
): WordOption[] {
  const declared = definitionsForSource(source);
  const order = [
    "Records",
    "Money",
    "Figures",
    "Dates",
    "Choices",
    "Yes or no",
    "Text",
  ];
  return [...fields]
    .sort((left, right) => {
      const apart =
        order.indexOf(heldBy(left, declared)) -
        order.indexOf(heldBy(right, declared));
      return apart !== 0 ? apart : left.label.localeCompare(right.label);
    })
    .map((field) => ({
      value: field.name,
      label: field.label,
      group: heldBy(field, declared),
    }));
}

/**
 * What the record type behind a source says about each of its fields.
 *
 * Keyed as a summary names its columns - as the database does - because that
 * is what a comparison carries: ``merchant_name``, and a relation by what it
 * is grouped by, ``category_obj__name``.
 */
export function definitionsForSource(
  source: ReportSource | undefined,
): Record<string, FilterDefinitionType> {
  const object = objectForReportSource(source?.name);
  const declared = getFilterDefinitionsForObject(
    object && "typename" in object ? object.typename : undefined,
  );
  const byColumn: Record<string, FilterDefinitionType> = {};
  for (const [key, definition] of Object.entries(declared)) {
    byColumn[key.replace(/[A-Z]/g, (capital) => `_${capital.toLowerCase()}`)] =
      definition;
  }
  return byColumn;
}

/** Which run of the list a column belongs in. */
function heldBy(
  field: ReportSourceField,
  declared: Record<string, FilterDefinitionType>,
): string {
  const named: Record<string, string> = {
    reference: "Records",
    references: "Records",
    currency: "Money",
    number: "Figures",
    date: "Dates",
    datetime: "Dates",
    enum: "Choices",
    boolean: "Yes or no",
  };
  const asDeclared = named[declared[field.name.split("__")[0]]?.type ?? ""];
  if (asDeclared) return asDeclared;
  // A column the registry says nothing about is read as the summary describes
  // it: how a chart would format it, which is the little that is known.
  if (field.name.includes("__")) return "Records";
  if (field.kind === "money") return "Money";
  if (field.kind === "date") return "Dates";
  return "Text";
}
